import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  ChevronRight,
  Camera,
  Mail,
  Phone,
  MapPin,
  Briefcase,
  Building,
  ShieldCheck,
  Shield,
  CheckCircle2,
  UserRound,
  LogOut,
} from "lucide-react";
import {
  getEmployeeById,
  changePassword,
} from "../../../api/hrm/employees/employeeInfo";
import Spinner from "../../../components/ui/Spinner";
import {
  showErrorToast,
  showSuccessToast,
  showConfirmDialog,
} from "../../../components/ConfirmDialog";
import { logout } from "../../../utils/auth";
import {
  MobilePageShell,
  MobilePageHeader,
  MobileSectionHeader,
  MobileNavGroup,
} from "../../../layouts/mobile/MobileNavPrimitives";

const validatePasswordStrength = (password) =>
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{6,}$/.test(password);

function MobileChangePasswordModal({ open, onClose, employeeId }) {
  const [oldPass, setOldPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [confirmPass, setConfirmPass] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setOldPass("");
    setNewPass("");
    setConfirmPass("");
    setError("");
    setSubmitting(false);
  }, [open]);

  const handleSubmit = async () => {
    setError("");
    if (!oldPass || !newPass || !confirmPass) {
      setError("Vui lòng nhập đủ các trường.");
      return;
    }
    if (!validatePasswordStrength(newPass)) {
      setError("Mật khẩu mới phải ≥6 ký tự, gồm chữ hoa, chữ thường và số.");
      return;
    }
    if (newPass !== confirmPass) {
      setError("Xác nhận mật khẩu không khớp.");
      return;
    }

    setSubmitting(true);
    try {
      await changePassword(employeeId, oldPass, newPass);
      showSuccessToast("Đổi mật khẩu thành công!");
      onClose();
    } catch (err) {
      const msg =
        (typeof err === "object" && err?.message) ||
        err?.error ||
        "Lỗi khi đổi mật khẩu";
      showErrorToast(msg);
    } finally {
      setSubmitting(false);
    }
  };

  if (typeof document === "undefined") return null;

  return createPortal(
    <AnimatePresence>
      {open && (
        <motion.div
          key="mobile-change-password-overlay"
          className="fixed inset-0 z-[300] flex items-end sm:items-center justify-center p-0 sm:p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.button
            type="button"
            aria-label="Đóng"
            className="absolute inset-0 bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
          />
          <motion.div
            role="dialog"
            aria-modal="true"
            aria-labelledby="mobile-change-pw-title"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", damping: 28, stiffness: 320 }}
            className="relative z-10 w-full max-w-md rounded-t-[1.5rem] sm:rounded-[1.5rem] bg-white shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 pt-4 pb-3 border-b border-gray-100">
              <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-200 sm:hidden" />
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-700">
                  <Shield size={18} strokeWidth={2.25} aria-hidden />
                </div>
                <div>
                  <h2
                    id="mobile-change-pw-title"
                    className="text-[15px] font-semibold text-gray-900"
                  >
                    Đổi mật khẩu
                  </h2>
                  <p className="text-[11px] text-gray-400">
                    ≥6 ký tự, có chữ hoa, thường và số
                  </p>
                </div>
              </div>
            </div>

            <div className="px-5 py-4 space-y-3">
              <input
                type="password"
                autoComplete="current-password"
                placeholder="Mật khẩu hiện tại"
                className="w-full rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-800 outline-none focus:border-rose-100 focus:bg-white focus:ring-2 focus:ring-rose-50"
                value={oldPass}
                onChange={(e) => {
                  setOldPass(e.target.value);
                  setError("");
                }}
              />
              <input
                type="password"
                autoComplete="new-password"
                placeholder="Mật khẩu mới"
                className="w-full rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-800 outline-none focus:border-rose-100 focus:bg-white focus:ring-2 focus:ring-rose-50"
                value={newPass}
                onChange={(e) => {
                  setNewPass(e.target.value);
                  setError("");
                }}
              />
              <input
                type="password"
                autoComplete="new-password"
                placeholder="Xác nhận mật khẩu mới"
                className="w-full rounded-xl border border-gray-100 bg-gray-50 px-4 py-3 text-sm text-gray-800 outline-none focus:border-rose-100 focus:bg-white focus:ring-2 focus:ring-rose-50"
                value={confirmPass}
                onChange={(e) => {
                  setConfirmPass(e.target.value);
                  setError("");
                }}
              />
              {error ? (
                <p className="text-[12px] font-medium text-rose-600 px-0.5">{error}</p>
              ) : null}
            </div>

            <div className="px-5 pb-6 pt-1 flex items-center justify-between gap-3">
              <button
                type="button"
                onClick={onClose}
                className="rounded-xl px-4 py-3 text-sm font-medium text-gray-500 active:bg-gray-50"
              >
                Hủy
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={handleSubmit}
                className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-5 py-3 text-sm font-semibold text-white shadow-sm shadow-rose-200/60 disabled:opacity-60 active:scale-[0.98]"
              >
                <CheckCircle2 className="h-4 w-4 shrink-0" strokeWidth={2.25} />
                {submitting ? "Đang lưu…" : "Xác nhận"}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
}

function InfoRow({ icon: Icon, label, value, tone = "slate" }) {
  const tones = {
    sky: "bg-sky-50 text-sky-700",
    emerald: "bg-emerald-50 text-emerald-700",
    amber: "bg-amber-50 text-amber-700",
    indigo: "bg-indigo-50 text-indigo-700",
    violet: "bg-violet-50 text-violet-700",
    rose: "bg-rose-50 text-rose-700",
    slate: "bg-slate-50 text-slate-600",
  };
  return (
    <div className="flex items-center gap-3 px-4 py-3.5">
      <div
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${tones[tone] || tones.slate}`}
      >
        <Icon size={16} strokeWidth={2} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[11px] text-gray-400">{label}</p>
        <p className="truncate text-sm font-medium text-gray-800">
          {value || "Chưa cập nhật"}
        </p>
      </div>
    </div>
  );
}

export default function MobileProfile({ user }) {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [pwModalOpen, setPwModalOpen] = useState(false);
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    const fetchProfile = async () => {
      let targetId = user?.id;
      if (!targetId) {
        try {
          targetId = JSON.parse(localStorage.getItem("user") || "{}")?.id;
        } catch {
          targetId = null;
        }
      }
      if (!targetId) {
        navigate("/login");
        return;
      }

      try {
        const data = await getEmployeeById(targetId);
        setProfile(data);
        setLoadError(false);
      } catch (error) {
        console.error("Lỗi tải profile:", error);
        showErrorToast("Không tải được hồ sơ cá nhân");
        setLoadError(true);
      }
    };
    fetchProfile();
  }, [user, navigate]);

  const handleLogout = async () => {
    const confirm = await showConfirmDialog({
      title: "Xác nhận đăng xuất",
      text: "Bạn có chắc chắn muốn rời khỏi hệ thống không?",
      confirmText: "Đăng xuất",
      cancelText: "Ở lại",
    });
    if (confirm.isConfirmed) {
      logout();
      window.location.href = "/login";
    }
  };

  if (!profile && !loadError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#F6F7FB]">
        <Spinner />
      </div>
    );
  }

  if (loadError && !profile) {
    return (
      <MobilePageShell>
        <MobilePageHeader
          title="Hồ sơ"
          subtitle="Không tải được dữ liệu"
          tone="rose"
        >
          <button
            type="button"
            onClick={() => navigate("/m/more")}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] font-medium text-gray-700"
          >
            <ArrowLeft size={14} />
            Về Thêm
          </button>
        </MobilePageHeader>
        <div className="px-5 pt-10 text-center text-[13px] text-gray-400">
          Thử lại sau hoặc đăng nhập lại.
        </div>
      </MobilePageShell>
    );
  }

  const displayTitle =
    profile.name ||
    [profile.last_name, profile.first_name].filter(Boolean).join(" ") ||
    "Người dùng";

  return (
    <MobilePageShell>
      <MobilePageHeader
        title="Hồ sơ"
        subtitle="Thông tin tài khoản của bạn"
        tone="sky"
      >
        <button
          type="button"
          onClick={() => navigate("/m/more")}
          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-[12px] font-medium text-gray-700 active:bg-gray-50"
        >
          <ArrowLeft size={14} />
          Về Thêm
        </button>
      </MobilePageHeader>

      <div className="space-y-5 px-5 pt-5 pb-6">
        {/* Hero */}
        <section className="relative overflow-hidden rounded-2xl border border-sky-100 bg-gradient-to-br from-sky-50 to-white p-5 shadow-sm">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-6 -top-8 h-24 w-24 rounded-full bg-sky-100/70 blur-2xl"
          />
          <div className="relative flex items-center gap-4">
            <div className="relative shrink-0">
              <div className="h-20 w-20 overflow-hidden rounded-2xl border-2 border-white bg-sky-100 shadow-md ring-1 ring-sky-100">
                {profile.avatar_url ? (
                  <img
                    src={profile.avatar_url}
                    className="h-full w-full object-cover"
                    alt=""
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-sky-400">
                    <UserRound size={36} />
                  </div>
                )}
              </div>
              <span className="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-xl border-2 border-white bg-sky-600 text-white shadow-sm">
                <Camera size={14} aria-hidden />
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="truncate text-lg font-semibold text-gray-900">
                {displayTitle}
              </h2>
              {profile.position ? (
                <p className="mt-1 inline-flex rounded-full border border-sky-100 bg-white/80 px-2.5 py-0.5 text-[11px] font-medium text-sky-700">
                  {profile.position}
                </p>
              ) : null}
              {profile.employee_code ? (
                <p className="mt-1.5 text-[11px] text-gray-400">
                  Mã NV: {profile.employee_code}
                </p>
              ) : null}
            </div>
          </div>
        </section>

        {/* Contact */}
        <section>
          <MobileSectionHeader title="Liên lạc" tone="sky" />
          <MobileNavGroup>
            <InfoRow icon={Mail} label="Email" value={profile.email} tone="sky" />
            <InfoRow
              icon={Phone}
              label="Số điện thoại"
              value={profile.phone}
              tone="emerald"
            />
            <InfoRow
              icon={MapPin}
              label="Địa chỉ"
              value={profile.address}
              tone="amber"
            />
          </MobileNavGroup>
        </section>

        {/* Org */}
        <section>
          <MobileSectionHeader title="Công việc & tổ chức" tone="indigo" />
          <MobileNavGroup>
            <InfoRow
              icon={Building}
              label="Chi nhánh"
              value={profile.branch_name || profile.branch?.name}
              tone="indigo"
            />
            <InfoRow
              icon={Briefcase}
              label="Phòng ban"
              value={profile.department_name || profile.department?.name}
              tone="violet"
            />
            <InfoRow
              icon={ShieldCheck}
              label="Mã nhân viên"
              value={profile.employee_code}
              tone="slate"
            />
          </MobileNavGroup>
        </section>

        {/* Security & account */}
        <section>
          <MobileSectionHeader title="Tài khoản" tone="rose" />
          <MobileNavGroup>
            <button
              type="button"
              onClick={() => setPwModalOpen(true)}
              className="flex w-full items-center gap-3 px-4 py-3.5 text-left active:bg-gray-50"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-700">
                <Shield size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-gray-800">Đổi mật khẩu</p>
                <p className="text-[11px] text-gray-400">Bảo mật đăng nhập</p>
              </div>
              <ChevronRight size={14} className="text-gray-300" />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="flex w-full items-center gap-3 px-4 py-3.5 text-left active:bg-rose-50/50"
            >
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-rose-50 text-rose-600">
                <LogOut size={16} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-rose-600">Đăng xuất</p>
              </div>
              <ChevronRight size={14} className="text-rose-300" />
            </button>
          </MobileNavGroup>
        </section>
      </div>

      <MobileChangePasswordModal
        open={pwModalOpen}
        onClose={() => setPwModalOpen(false)}
        employeeId={profile.id}
      />
    </MobilePageShell>
  );
}
